import { RewardStoreItemOrderField } from "../../types/__generated__/globalTypes";

export interface RewardsStoreFilters {
  minPoints?: number;
  maxPoints?: number;
  pointRanges?: number[];
  colourIds: number[];
}

export const REWARD_POINTS_RANGES: { id: number; min: number; max?: number }[] = [
  { id: 500, min: 0, max: 500 },
  { id: 1000, min: 501, max: 1000 },
  { id: 1500, min: 1001, max: 1500 },
  { id: 2000, min: 1501, max: 2000 },
  { id: 2500, min: 2001, max: 2500 },
  { id: 3000, min: 2501 },
];

export const getRewardPointsBounds = (pointRanges: number[]) => {
  const selected = REWARD_POINTS_RANGES.filter((range) => pointRanges.includes(range.id));
  return {
    minPoints: selected[0]?.min,
    maxPoints: selected[selected.length - 1]?.max,
  };
};

export const getSelectedRewardPointRanges = (filters: RewardsStoreFilters) => {
  if (filters.pointRanges?.length) return filters.pointRanges;
  if (filters.minPoints === undefined && filters.maxPoints === undefined) return [];
  // Existing links with custom bounds open with the corresponding bands selected.
  return REWARD_POINTS_RANGES.filter(
    (range) =>
      range.min <= (filters.maxPoints ?? Infinity) && (range.max ?? Infinity) >= (filters.minPoints ?? 0)
  ).map((range) => range.id);
};

export const matchesRewardPointRanges = (pointCost: number, pointRanges: number[] = []) =>
  !pointRanges.length ||
  REWARD_POINTS_RANGES.some(
    (range) =>
      pointRanges.includes(range.id) && pointCost >= range.min && pointCost <= (range.max ?? Infinity)
  );

export const REWARD_SORT_FIELDS = {
  [RewardStoreItemOrderField.POINTS_ASC]: "product__point_cost",
  [RewardStoreItemOrderField.POINTS_DESC]: "-product__point_cost",
  [RewardStoreItemOrderField.NEWEST_FIRST]: "-product__created_date",
};

const parsePoints = (value: string | null): number | undefined => {
  if (!value?.trim()) return undefined;
  const points = Number(value);
  return Number.isSafeInteger(points) && points >= 0 ? points : undefined;
};

export const getRewardsStoreOptions = (search: string) => {
  const params = new URLSearchParams(search);
  const sort = params.get("sort") as RewardStoreItemOrderField | null;
  const orderBy = sort && Object.values(RewardStoreItemOrderField).includes(sort) ? sort : undefined;
  const requestedRanges = (params.get("points") || "").split(",").map(Number);
  const pointRanges = REWARD_POINTS_RANGES.filter((range) => requestedRanges.includes(range.id)).map(
    (range) => range.id
  );
  const filters: RewardsStoreFilters = {
    minPoints: parsePoints(params.get("minPoints")),
    maxPoints: parsePoints(params.get("maxPoints")),
    ...(pointRanges.length ? { ...getRewardPointsBounds(pointRanges), pointRanges } : {}),
    colourIds: [...new Set((params.get("colours") || "").split(",").map(Number))].filter(
      (id) => Number.isSafeInteger(id) && id > 0
    ),
  };
  return { orderBy, filters };
};

export const getRewardsStoreSearch = (filters: RewardsStoreFilters, orderBy?: RewardStoreItemOrderField) => {
  const params = new URLSearchParams();
  if (orderBy) params.set("sort", orderBy);
  if (filters.pointRanges?.length) {
    params.set("points", filters.pointRanges.join(","));
  } else {
    if (filters.minPoints !== undefined) params.set("minPoints", String(filters.minPoints));
    if (filters.maxPoints !== undefined) params.set("maxPoints", String(filters.maxPoints));
  }
  if (filters.colourIds.length) params.set("colours", filters.colourIds.join(","));
  const search = params.toString();
  return search ? `?${search}` : "";
};

export const getRewardsFilterCount = (filters: RewardsStoreFilters) =>
  filters.colourIds.length +
  (filters.pointRanges?.length || Number(filters.minPoints !== undefined || filters.maxPoints !== undefined));
